/**
 * A catalogue model's lifecycle at Anthropic (TMMPDM-0105, TMMAPI-0172 F.17): `deprecated` is a
 * warning chip, "Retires on {date}" from `retiresAt` (formatting only) or "Deprecated" without it;
 * `retired` is an error chip; `active`, absent or a value this build doesn't name draws nothing.
 * The chip comes from `lifecycle` alone. Used on the Models tab and, through a `modelId` lookup
 * (a display join), on the tier tabs.
 */
import { formatDate } from '@/format';
import { useCopy } from '@/hooks/useCopy';
import type { AiModelCatalogRow } from '@/api/tmm/types';
import { StatusChip } from '@/status';

export function LifecycleChip({
  row,
  locale,
}: {
  row: AiModelCatalogRow | undefined;
  locale: string;
}) {
  const { t } = useCopy();
  if (row?.lifecycle === 'deprecated') {
    return typeof row.retiresAt === 'string' && row.retiresAt !== '' ? (
      <StatusChip
        tone="warning"
        label={t('ai.catalog.lifecycle.retiresOn', 'Retires on {{date}}', {
          date: formatDate(row.retiresAt, locale),
        })}
      />
    ) : (
      <StatusChip tone="warning" label={t('ai.catalog.lifecycle.deprecated', 'Deprecated')} />
    );
  }
  if (row?.lifecycle === 'retired') {
    return (
      <StatusChip tone="error" label={t('ai.catalog.lifecycle.retired', 'Retired by Anthropic')} />
    );
  }
  return null;
}
